import React, { useId } from 'react';
import { Form, Radio } from 'semantic-ui-react';

export interface CmRadioOption<T extends string> {
  value: T;
  label: React.ReactNode;
}

export interface CmRadioGroupProps<T extends string> {
  name: string;
  value: T | null;
  options: readonly CmRadioOption<T>[];
  onChange: (value: T) => void;
  legend?: React.ReactNode;
  disabled?: boolean;
  testId?: string;
}

// We deliberately avoid <fieldset>/<legend> here: Form.Field renders a <div>,
// and <legend> is only valid as a direct child of <fieldset>. Instead we mark
// the container as role="radiogroup" and reference a visible label via
// aria-labelledby — assistive tech reads the group name correctly without
// invalid HTML.
export function CmRadioGroup<T extends string>({
  name,
  value,
  options,
  onChange,
  legend,
  disabled = false,
  testId,
}: CmRadioGroupProps<T>): React.ReactElement {
  const reactId = useId();
  const legendId = legend ? `${reactId}-legend` : undefined;
  return (
    <Form.Field data-testid={testId} role="radiogroup" aria-labelledby={legendId}>
      {legend ? <div id={legendId}>{legend}</div> : null}
      {options.map((option) => (
        <Form.Field key={option.value}>
          <Radio
            name={name}
            label={option.label}
            value={option.value}
            checked={value === option.value}
            disabled={disabled}
            onChange={() => onChange(option.value)}
          />
        </Form.Field>
      ))}
    </Form.Field>
  );
}

export default CmRadioGroup;
